import moment from 'moment';

/**
 * Date helpers shared by the dashboard and history pages.
 *
 * Two kinds of values come out of the API:
 *  - DATE columns (expires_on, defer_on, renewal dates) arrive as
 *    'YYYY-MM-DD' or 'YYYY-MM-DDT00:00:00.000Z'. They are calendar dates and
 *    must be read in UTC — parsing them as local instants shifts the day
 *    backwards in western timezones (the Enrollment card had this bug once).
 *  - DATETIME columns (completed_at, start_at, last_activity_at) are real
 *    instants and are formatted in the viewer's local time.
 */

// Calendar date -> 'YYYY-MM-DD' (or null)
export function toDateOnly(value) {
    return value ? moment.utc(value).format('YYYY-MM-DD') : null;
}

// Calendar date as a LOCAL-midnight moment, so day math matches the wall
// calendar the student is looking at
export function dateOnly(value) {
    const d = toDateOnly(value);
    return d ? moment(d, 'YYYY-MM-DD') : null;
}

export function formatDateOnly(value, format = 'MMMM Do, YYYY') {
    return value ? moment.utc(value).format(format) : '';
}

// Whole days from today to a calendar date: 0 = today, negative = past
export function daysUntil(value) {
    const d = dateOnly(value);
    return d ? d.startOf('day').diff(moment().startOf('day'), 'days') : null;
}

export function isPastDateOnly(value) {
    const n = daysUntil(value);
    return n !== null && n < 0;
}

// "Just now" / "Today" / "Yesterday" / "3 days ago" for instants
export function timeAgo(value) {
    if (!value) return '';
    const m = moment(value);
    if (!m.isValid()) return '';
    const now = moment();
    if (now.diff(m, 'minutes') < 1) return 'Just now';
    if (m.isSame(now, 'day')) return 'Today';
    if (m.isSame(now.clone().subtract(1, 'day'), 'day')) return 'Yesterday';
    return m.fromNow();
}

// "March 3, 2026", "March 3 - 5, 2026", "Mar 30 - Apr 2, 2026", ...
export function formatDateRange(start, end) {
    const s = moment(start);
    const e = end ? moment(end) : s;
    if (s.isSame(e, 'day')) {
        return s.format('MMMM D, YYYY');
    } else if (s.isSame(e, 'month')) {
        return s.format('MMMM D - ') + e.format('D, YYYY');
    } else if (s.isSame(e, 'year')) {
        return s.format('MMM D - ') + e.format('MMM D, YYYY');
    } else {
        return s.format('MMM D, YYYY - ') + e.format('MMM D, YYYY');
    }
}
